/* eslint-disable */
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import ChevronRightOutlined from '@mui-symbols-material/w400/ChevronRightOutlined';
import { Link } from 'react-router-dom';
import HomeOutlined from '@mui-symbols-material/w400/HomeOutlined';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import SaveOutlined from '@mui-symbols-material/w400/SaveOutlined';
import Upload2Outlined from '@mui-symbols-material/w400/Upload2Outlined';
import CompareArrowsOutlined from '@mui-symbols-material/w400/CompareArrowsOutlined';
import SearchOutlined from '@mui-symbols-material/w400/SearchOutlined';
import ViewListOutlined from '@mui-symbols-material/w400/ViewListOutlined';
import CodeOutlined from '@mui-symbols-material/w400/CodeOutlined';
import { styled } from '@mui/material/styles';
import KeyboardArrowDownOutlined from '@mui-symbols-material/w400/KeyboardArrowDownOutlined';
import KeyboardArrowRightOutlined from '@mui-symbols-material/w400/KeyboardArrowRightOutlined';
import EditOutlined from '@mui-symbols-material/w400/EditOutlined';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Collapse from '@mui/material/Collapse';
import AddOutlined from '@mui-symbols-material/w400/AddOutlined';
import DownloadOutlined from '@mui-symbols-material/w400/DownloadOutlined';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { loadTranslationFiles, saveTranslationFiles, normalizeTranslationStructure, findMissingTranslations } from './translationService';

// Styled components
const EditorContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  height: 'calc(100vh - 250px)',
  display: 'flex',
  flexDirection: 'column'
}));

const JsonEditor = styled(TextField)(() => ({
  flex: 1,
  '& .MuiInputBase-root': {
    fontFamily: 'monospace',
    fontSize: '14px'
  }
}));

const ActionButton = styled(Button)(() => ({
  margin: '8px'
}));

const TreeContainer = styled(Box)(() => ({
  flex: 1,
  overflow: 'auto',
  padding: '8px'
}));

const TreeItemLabel = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '4px 0',
  width: '100%'
}));

const TreeItemValue = styled(TextField)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  width: '60%'
}));

const NestedList = styled(List)(({ theme }) => ({
  paddingLeft: theme.spacing(2)
}));

// Interface für TreeItem-Daten
interface TreeItemData {
  id: string;
  name: string;
  value?: string;
  children?: TreeItemData[];
}

// Interface für Dialog-Daten
interface AddTranslationDialogData {
  open: boolean;
  parentId: string;
  parentName: string;
  isRoot: boolean;
  language: 'de' | 'en';
  type: 'node' | 'leaf';
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: Readonly<TabPanelProps>) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`translation-tabpanel-${index}`}
      aria-labelledby={`translation-tab-${index}`}
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      {...other}>
      {value === index && <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>{children}</Box>}
    </div>
  );
}

// Custom TreeView component using MUI List
interface TreeViewItemProps {
  item: TreeItemData;
  language: 'de' | 'en';
  onValueChange: (id: string, value: string) => void;
  onAddNode: (parentId: string, parentName: string, language: 'de' | 'en') => void;
  onAddLeaf: (parentId: string, parentName: string, language: 'de' | 'en') => void;
}

function TreeViewItem({ item, language, onValueChange, onAddNode, onAddLeaf }: Readonly<TreeViewItemProps>) {
  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    setOpen(!open);
  };

  return (
    <>
      <ListItem
        sx={{
          pl: 0,
          borderLeft: '1px solid #eee',
          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
        }}>
        <TreeItemLabel>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {item.children && item.children.length > 0 && (
              <IconButton size="small" onClick={handleToggle}>
                {open ? <KeyboardArrowDownOutlined /> : <KeyboardArrowRightOutlined />}
              </IconButton>
            )}
            <Typography variant="body2" sx={{ fontWeight: item.children ? 'bold' : 'normal' }}>
              {item.name}
            </Typography>
            <Box sx={{ ml: 1 }}>
              <IconButton size="small" title="Untergruppe hinzufügen" onClick={() => onAddNode(item.id, item.name, language)}>
                <AddOutlined fontSize="small" />
              </IconButton>
              <IconButton size="small" title="Übersetzung hinzufügen" onClick={() => onAddLeaf(item.id, item.name, language)}>
                <EditOutlined fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          {item.value !== undefined && (
            <TreeItemValue
              size="small"
              value={item.value}
              onChange={e => onValueChange(item.id, e.target.value)}
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" size="small">
                      <EditOutlined fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          )}
        </TreeItemLabel>
      </ListItem>
      {item.children && item.children.length > 0 && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <NestedList>
            {item.children.map(child => (
              <TreeViewItem
                key={child.id}
                item={child}
                language={language}
                onValueChange={onValueChange}
                onAddNode={onAddNode}
                onAddLeaf={onAddLeaf}
              />
            ))}
          </NestedList>
        </Collapse>
      )}
    </>
  );
}

export function TranslationEditorPage() {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [deTranslation, setDeTranslation] = useState('');
  const [enTranslation, setEnTranslation] = useState('');
  const [deTreeData, setDeTreeData] = useState<TreeItemData[]>([]);
  const [enTreeData, setEnTreeData] = useState<TreeItemData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'json' | 'tree'>('json');
  const [missingKeys, setMissingKeys] = useState<{ missingInDe: string[]; missingInEn: string[] }>({
    missingInDe: [],
    missingInEn: []
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info'
  });

  const [dialogData, setDialogData] = useState<AddTranslationDialogData>({
    open: false,
    parentId: '',
    parentName: '',
    isRoot: false,
    language: 'de',
    type: 'leaf'
  });

  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyValue, setNewKeyValue] = useState('');
  const [keyType, setKeyType] = useState<'node' | 'leaf'>('leaf');

  // Funktion zum Konvertieren von JSON in TreeView-Daten
  const convertJsonToTreeData = useCallback((jsonObj: any, parentKey = ''): TreeItemData[] => {
    if (!jsonObj || typeof jsonObj !== 'object') return [];

    return Object.entries(jsonObj).map(([key, value]) => {
      const currentKey = parentKey ? `${parentKey}.${key}` : key;

      if (value && typeof value === 'object') {
        // Es ist ein Objekt, rekursiv verarbeiten
        return {
          id: currentKey,
          name: key,
          children: convertJsonToTreeData(value, currentKey)
        };
      } else {
        // Es ist ein Wert
        return {
          id: currentKey,
          name: key,
          value: value as string
        };
      }
    });
  }, []);

  // Funktion zum Aktualisieren eines Wertes im TreeData
  const updateTreeDataValue = useCallback((treeData: TreeItemData[], id: string, newValue: string): TreeItemData[] => {
    return treeData.map(item => {
      if (item.id === id) {
        return { ...item, value: newValue };
      } else if (item.children) {
        return { ...item, children: updateTreeDataValue(item.children, id, newValue) };
      }
      return item;
    });
  }, []);

  // Funktion zum Konvertieren von TreeView-Daten zurück in JSON
  const convertTreeDataToJson = useCallback((treeData: TreeItemData[]): any => {
    const result: any = {};

    treeData.forEach(item => {
      const keys = item.id.split('.');
      const lastKey = keys[keys.length - 1];

      if (item.children) {
        // Es ist ein Objekt
        result[lastKey] = convertTreeDataToJson(item.children);
      } else {
        // Es ist ein Wert
        result[lastKey] = item.value;
      }
    });

    return result;
  }, []);

  // Funktion zum Laden der Übersetzungsdateien
  const loadTranslations = useCallback(async () => {
    setIsLoading(true);
    try {
      const { de, en } = await loadTranslationFiles();

      setDeTranslation(JSON.stringify(de, null, 2));
      setEnTranslation(JSON.stringify(en, null, 2));

      // Konvertiere JSON in TreeView-Daten
      setDeTreeData(convertJsonToTreeData(de));
      setEnTreeData(convertJsonToTreeData(en));

      // Finde fehlende Übersetzungen
      const missing = findMissingTranslations(de, en);
      setMissingKeys(missing);

      setSnackbar({
        open: true,
        message: 'Übersetzungsdateien erfolgreich geladen',
        severity: 'success'
      });
    } catch (error) {
      console.error('Fehler beim Laden der Übersetzungsdateien:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Laden der Übersetzungsdateien',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  }, [convertJsonToTreeData]);

  // Funktion zum Speichern der Übersetzungsdateien
  const saveTranslations = useCallback(async () => {
    setIsLoading(true);
    try {
      let deData, enData;

      if (viewMode === 'json') {
        // Validiere JSON
        deData = JSON.parse(deTranslation);
        enData = JSON.parse(enTranslation);
      } else {
        // Konvertiere TreeView-Daten zurück in JSON
        deData = convertTreeDataToJson(deTreeData);
        enData = convertTreeDataToJson(enTreeData);

        // Aktualisiere auch die JSON-Darstellung
        setDeTranslation(JSON.stringify(deData, null, 2));
        setEnTranslation(JSON.stringify(enData, null, 2));
      }

      // Speichere Übersetzungen
      const success = await saveTranslationFiles(deData, enData);

      if (success) {
        setSnackbar({
          open: true,
          message: 'Übersetzungsdateien erfolgreich gespeichert',
          severity: 'success'
        });
      } else {
        throw new Error('Fehler beim Speichern der Übersetzungsdateien');
      }
    } catch (error) {
      console.error('Fehler beim Speichern der Übersetzungsdateien:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Speichern der Übersetzungsdateien: Ungültiges JSON',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  }, [deTranslation, enTranslation, viewMode, deTreeData, enTreeData, convertTreeDataToJson]);

  // Funktion zum Herunterladen der Übersetzungsdateien
  const downloadTranslation = useCallback(
    (language: 'de' | 'en') => {
      try {
        let content;

        if (viewMode === 'json') {
          content = language === 'de' ? deTranslation : enTranslation;
        } else {
          // Konvertiere TreeView-Daten zurück in JSON
          const data = language === 'de' ? convertTreeDataToJson(deTreeData) : convertTreeDataToJson(enTreeData);
          content = JSON.stringify(data, null, 2);
        }

        const blob = new Blob([content], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `translation_${language}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error(`Fehler beim Herunterladen der ${language} Übersetzungsdatei:`, error);
        setSnackbar({
          open: true,
          message: `Fehler beim Herunterladen der ${language} Übersetzungsdatei`,
          severity: 'error'
        });
      }
    },
    [deTranslation, enTranslation, viewMode, deTreeData, enTreeData, convertTreeDataToJson]
  );

  // Funktion zum Hochladen einer Übersetzungsdatei
  const uploadTranslation = useCallback(
    (language: 'de' | 'en') => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'application/json';

      input.onchange = e => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = event => {
          try {
            const content = event.target?.result as string;
            // Validiere JSON
            const jsonData = JSON.parse(content);

            if (language === 'de') {
              setDeTranslation(content);
              setDeTreeData(convertJsonToTreeData(jsonData));
            } else {
              setEnTranslation(content);
              setEnTreeData(convertJsonToTreeData(jsonData));
            }

            setSnackbar({
              open: true,
              message: `${language.toUpperCase()} Übersetzungsdatei erfolgreich geladen`,
              severity: 'success'
            });
          } catch (error) {
            console.error(`Fehler beim Parsen der ${language} Übersetzungsdatei:`, error);
            setSnackbar({
              open: true,
              message: `Fehler beim Parsen der ${language} Übersetzungsdatei: Ungültiges JSON`,
              severity: 'error'
            });
          }
        };

        reader.readAsText(file);
      };

      input.click();
    },
    [convertJsonToTreeData]
  );

  // Funktion zum Vereinheitlichen der Struktur
  const normalizeStructure = useCallback(() => {
    try {
      let deData, enData;

      if (viewMode === 'json') {
        deData = JSON.parse(deTranslation);
        enData = JSON.parse(enTranslation);
      } else {
        deData = convertTreeDataToJson(deTreeData);
        enData = convertTreeDataToJson(enTreeData);
      }

      // Vereinheitliche Struktur
      const { de: normalizedDe, en: normalizedEn } = normalizeTranslationStructure(deData, enData);

      // Aktualisierte Übersetzungen setzen
      setDeTranslation(JSON.stringify(normalizedDe, null, 2));
      setEnTranslation(JSON.stringify(normalizedEn, null, 2));

      // Aktualisiere auch TreeView-Daten
      setDeTreeData(convertJsonToTreeData(normalizedDe));
      setEnTreeData(convertJsonToTreeData(normalizedEn));

      // Finde fehlende Übersetzungen
      const missing = findMissingTranslations(normalizedDe, normalizedEn);
      setMissingKeys(missing);

      setSnackbar({
        open: true,
        message: 'Struktur erfolgreich vereinheitlicht',
        severity: 'success'
      });
    } catch (error) {
      console.error('Fehler beim Vereinheitlichen der Struktur:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Vereinheitlichen der Struktur: Ungültiges JSON',
        severity: 'error'
      });
    }
  }, [deTranslation, enTranslation, viewMode, deTreeData, enTreeData, convertJsonToTreeData, convertTreeDataToJson]);

  // Funktion zum Finden fehlender Übersetzungen
  const findMissing = useCallback(() => {
    try {
      let deData, enData;

      if (viewMode === 'json') {
        deData = JSON.parse(deTranslation);
        enData = JSON.parse(enTranslation);
      } else {
        deData = convertTreeDataToJson(deTreeData);
        enData = convertTreeDataToJson(enTreeData);
      }

      // Finde fehlende Übersetzungen
      const missing = findMissingTranslations(deData, enData);
      setMissingKeys(missing);

      setSnackbar({
        open: true,
        message: `Fehlende Übersetzungen gefunden: ${missing.missingInDe.length} in DE, ${missing.missingInEn.length} in EN`,
        severity: 'info'
      });
    } catch (error) {
      console.error('Fehler beim Finden fehlender Übersetzungen:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Finden fehlender Übersetzungen: Ungültiges JSON',
        severity: 'error'
      });
    }
  }, [deTranslation, enTranslation, viewMode, deTreeData, enTreeData, convertTreeDataToJson]);

  // Funktion zum Aktualisieren eines Wertes im TreeView
  const handleTreeItemValueChange = useCallback(
    (language: 'de' | 'en', id: string, newValue: string) => {
      if (language === 'de') {
        setDeTreeData(prev => updateTreeDataValue(prev, id, newValue));
      } else {
        setEnTreeData(prev => updateTreeDataValue(prev, id, newValue));
      }
    },
    [updateTreeDataValue]
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const toggleViewMode = () => {
    if (viewMode === 'json') {
      try {
        // Beim Wechsel von JSON zu Tree, aktualisiere die TreeView-Daten
        const deData = JSON.parse(deTranslation);
        const enData = JSON.parse(enTranslation);
        setDeTreeData(convertJsonToTreeData(deData));
        setEnTreeData(convertJsonToTreeData(enData));
        setViewMode('tree');
      } catch (error) {
        console.error('Fehler beim Konvertieren von JSON zu TreeView:', error);
        setSnackbar({
          open: true,
          message: 'Fehler beim Konvertieren von JSON zu TreeView: Ungültiges JSON',
          severity: 'error'
        });
      }
    } else {
      try {
        // Beim Wechsel von Tree zu JSON, aktualisiere die JSON-Darstellung
        const deData = convertTreeDataToJson(deTreeData);
        const enData = convertTreeDataToJson(enTreeData);
        setDeTranslation(JSON.stringify(deData, null, 2));
        setEnTranslation(JSON.stringify(enData, null, 2));
        setViewMode('json');
      } catch (error) {
        console.error('Fehler beim Konvertieren von TreeView zu JSON:', error);
        setSnackbar({
          open: true,
          message: 'Fehler beim Konvertieren von TreeView zu JSON',
          severity: 'error'
        });
      }
    }
  };

  // Funktion zum Hinzufügen eines neuen Knotens (Gruppe) zum TreeData
  const addNodeToTreeData = useCallback((treeData: TreeItemData[], parentId: string, newNodeName: string): TreeItemData[] => {
    if (parentId === '') {
      // Füge auf Root-Ebene hinzu
      return [
        ...treeData,
        {
          id: newNodeName,
          name: newNodeName,
          children: []
        }
      ];
    }

    return treeData.map(item => {
      if (item.id === parentId) {
        // Füge als Kind des aktuellen Elements hinzu
        return {
          ...item,
          children: [
            ...(item.children || []),
            {
              id: `${parentId}.${newNodeName}`,
              name: newNodeName,
              children: []
            }
          ]
        };
      } else if (item.children) {
        // Rekursiv in Kindern suchen
        return {
          ...item,
          children: addNodeToTreeData(item.children, parentId, newNodeName)
        };
      }
      return item;
    });
  }, []);

  // Funktion zum Hinzufügen eines neuen Blatts (Übersetzung) zum TreeData
  const addLeafToTreeData = useCallback((treeData: TreeItemData[], parentId: string, newLeafName: string, newLeafValue: string): TreeItemData[] => {
    if (parentId === '') {
      // Füge auf Root-Ebene hinzu
      return [
        ...treeData,
        {
          id: newLeafName,
          name: newLeafName,
          value: newLeafValue
        }
      ];
    }

    return treeData.map(item => {
      if (item.id === parentId) {
        // Füge als Kind des aktuellen Elements hinzu
        return {
          ...item,
          children: [
            ...(item.children || []),
            {
              id: `${parentId}.${newLeafName}`,
              name: newLeafName,
              value: newLeafValue
            }
          ]
        };
      } else if (item.children) {
        // Rekursiv in Kindern suchen
        return {
          ...item,
          children: addLeafToTreeData(item.children, parentId, newLeafName, newLeafValue)
        };
      }
      return item;
    });
  }, []);

  // Handler für das Öffnen des Dialogs zum Hinzufügen eines Knotens
  const handleAddNode = useCallback((parentId: string, parentName: string, language: 'de' | 'en') => {
    setDialogData({
      open: true,
      parentId,
      parentName,
      isRoot: parentId === '',
      language,
      type: 'node'
    });
    setKeyType('node');
    setNewKeyName('');
    setNewKeyValue('');
  }, []);

  // Handler für das Öffnen des Dialogs zum Hinzufügen eines Blatts
  const handleAddLeaf = useCallback((parentId: string, parentName: string, language: 'de' | 'en') => {
    setDialogData({
      open: true,
      parentId,
      parentName,
      isRoot: parentId === '',
      language,
      type: 'leaf'
    });
    setKeyType('leaf');
    setNewKeyName('');
    setNewKeyValue('');
  }, []);

  // Handler für das Schließen des Dialogs
  const handleCloseDialog = useCallback(() => {
    setDialogData(prev => ({ ...prev, open: false }));
  }, []);

  // Handler für das Hinzufügen einer neuen Übersetzung
  const handleAddTranslation = useCallback(() => {
    const { parentId, language, type } = dialogData;

    if (!newKeyName.trim()) {
      setSnackbar({
        open: true,
        message: 'Bitte geben Sie einen Schlüsselnamen ein',
        severity: 'error'
      });
      return;
    }

    if (type === 'leaf' && !newKeyValue.trim()) {
      setSnackbar({
        open: true,
        message: 'Bitte geben Sie einen Wert ein',
        severity: 'error'
      });
      return;
    }

    try {
      if (language === 'de') {
        if (type === 'node') {
          setDeTreeData(prev => addNodeToTreeData(prev, parentId, newKeyName));
        } else {
          setDeTreeData(prev => addLeafToTreeData(prev, parentId, newKeyName, newKeyValue));
        }
      } else if (language === 'en') {
        if (type === 'node') {
          setEnTreeData(prev => addNodeToTreeData(prev, parentId, newKeyName));
        } else {
          setEnTreeData(prev => addLeafToTreeData(prev, parentId, newKeyName, newKeyValue));
        }
      }

      setSnackbar({
        open: true,
        message: type === 'node' ? 'Neue Gruppe erfolgreich hinzugefügt' : 'Neue Übersetzung erfolgreich hinzugefügt',
        severity: 'success'
      });

      handleCloseDialog();
    } catch (error) {
      console.error('Fehler beim Hinzufügen:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Hinzufügen',
        severity: 'error'
      });
    }
  }, [dialogData, newKeyName, newKeyValue, addNodeToTreeData, addLeafToTreeData, handleCloseDialog]);

  // Handler für das Hinzufügen einer neuen Root-Übersetzung
  const handleAddRootTranslation = useCallback(
    (language: 'de' | 'en') => {
      if (language === 'de') {
        handleAddNode('', 'Root', 'de');
      } else {
        handleAddNode('', 'Root', 'en');
      }
    },
    [handleAddNode]
  );

  return (
    <>
      <Breadcrumbs separator={<ChevronRightOutlined />}>
        <Link to="/">
          <HomeOutlined />
        </Link>
        <Link to="/translation-editor">{t('breadCrumbs.translationEditor.page')}</Link>
      </Breadcrumbs>

      <Box sx={{ p: 2 }}>
        <Typography variant="h4" gutterBottom>
          {t('breadCrumbs.translationEditor.page')}
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={8}>
            <ActionButton variant="contained" startIcon={<Upload2Outlined />} onClick={loadTranslations} disabled={isLoading}>
              Übersetzungen laden
            </ActionButton>
            <ActionButton variant="contained" color="primary" startIcon={<SaveOutlined />} onClick={saveTranslations} disabled={isLoading}>
              Änderungen speichern
            </ActionButton>
            <ActionButton variant="outlined" startIcon={<CompareArrowsOutlined />} onClick={normalizeStructure} disabled={isLoading}>
              Struktur vereinheitlichen
            </ActionButton>
            <ActionButton variant="outlined" startIcon={<SearchOutlined />} onClick={findMissing} disabled={isLoading}>
              Fehlende Übersetzungen finden
            </ActionButton>
            <ActionButton
              variant="outlined"
              startIcon={viewMode === 'json' ? <ViewListOutlined /> : <CodeOutlined />}
              onClick={toggleViewMode}
              disabled={isLoading}>
              {viewMode === 'json' ? 'Tree-Ansicht' : 'JSON-Ansicht'}
            </ActionButton>
          </Grid>
        </Grid>

        {(missingKeys.missingInDe.length > 0 || missingKeys.missingInEn.length > 0) && (
          <Paper sx={{ p: 2, mb: 2 }} elevation={2}>
            <Typography variant="h6" gutterBottom>
              Fehlende Übersetzungen
            </Typography>

            {missingKeys.missingInDe.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" color="error">
                  Fehlend in DE ({missingKeys.missingInDe.length}):
                </Typography>
                <Box sx={{ maxHeight: '100px', overflow: 'auto', pl: 2 }}>
                  {missingKeys.missingInDe.map(key => (
                    <Typography key={key} variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {key}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {missingKeys.missingInEn.length > 0 && (
              <Box>
                <Typography variant="subtitle1" color="error">
                  Fehlend in EN ({missingKeys.missingInEn.length}):
                </Typography>
                <Box sx={{ maxHeight: '100px', overflow: 'auto', pl: 2 }}>
                  {missingKeys.missingInEn.map(key => (
                    <Typography key={key} variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {key}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Paper>
        )}

        <EditorContainer elevation={3}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="translation tabs">
              <Tab label="Deutsch (DE)" id="translation-tab-0" aria-controls="translation-tabpanel-0" />
              <Tab label="Englisch (EN)" id="translation-tab-1" aria-controls="translation-tabpanel-1" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                startIcon={<AddOutlined />}
                onClick={() => handleAddRootTranslation('de')}
                disabled={isLoading || viewMode !== 'tree'}>
                Neue Übersetzung hinzufügen
              </Button>
              <Box>
                <Button variant="outlined" startIcon={<Upload2Outlined />} onClick={() => uploadTranslation('de')} sx={{ mr: 1 }} disabled={isLoading}>
                  DE Datei hochladen
                </Button>
                <Button variant="outlined" startIcon={<DownloadOutlined />} onClick={() => downloadTranslation('de')} disabled={isLoading}>
                  DE Datei herunterladen
                </Button>
              </Box>
            </Box>

            {viewMode === 'json' ? (
              <JsonEditor
                multiline
                fullWidth
                variant="outlined"
                value={deTranslation}
                onChange={e => setDeTranslation(e.target.value)}
                disabled={isLoading}
                placeholder="Hier wird die deutsche Übersetzungsdatei angezeigt..."
              />
            ) : (
              <TreeContainer>
                <List sx={{ width: '100%' }}>
                  {deTreeData.map(item => (
                    <TreeViewItem
                      key={item.id}
                      item={item}
                      language="de"
                      onValueChange={(id, value) => handleTreeItemValueChange('de', id, value)}
                      onAddNode={handleAddNode}
                      onAddLeaf={handleAddLeaf}
                    />
                  ))}
                </List>
              </TreeContainer>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                startIcon={<AddOutlined />}
                onClick={() => handleAddRootTranslation('en')}
                disabled={isLoading || viewMode !== 'tree'}>
                Neue Übersetzung hinzufügen
              </Button>
              <Box>
                <Button variant="outlined" startIcon={<Upload2Outlined />} onClick={() => uploadTranslation('en')} sx={{ mr: 1 }} disabled={isLoading}>
                  EN Datei hochladen
                </Button>
                <Button variant="outlined" startIcon={<DownloadOutlined />} onClick={() => downloadTranslation('en')} disabled={isLoading}>
                  EN Datei herunterladen
                </Button>
              </Box>
            </Box>

            {viewMode === 'json' ? (
              <JsonEditor
                multiline
                fullWidth
                variant="outlined"
                value={enTranslation}
                onChange={e => setEnTranslation(e.target.value)}
                disabled={isLoading}
                placeholder="Hier wird die englische Übersetzungsdatei angezeigt..."
              />
            ) : (
              <TreeContainer>
                <List sx={{ width: '100%' }}>
                  {enTreeData.map(item => (
                    <TreeViewItem
                      key={item.id}
                      item={item}
                      language="en"
                      onValueChange={(id, value) => handleTreeItemValueChange('en', id, value)}
                      onAddNode={handleAddNode}
                      onAddLeaf={handleAddLeaf}
                    />
                  ))}
                </List>
              </TreeContainer>
            )}
          </TabPanel>
        </EditorContainer>

        {/* Dialog zum Hinzufügen einer neuen Übersetzung */}
        <Dialog open={dialogData.open} onClose={handleCloseDialog}>
          <DialogTitle>{dialogData.type === 'node' ? 'Neue Gruppe hinzufügen' : 'Neue Übersetzung hinzufügen'}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {(() => {
                const itemType = dialogData.type === 'node' ? 'Gruppe' : 'Übersetzung';
                return dialogData.isRoot
                  ? `Fügen Sie eine neue ${itemType} auf der obersten Ebene hinzu.`
                  : `Fügen Sie eine neue ${itemType} zu "${dialogData.parentName}" hinzu.`;
              })()}
            </DialogContentText>

            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <InputLabel id="key-type-label">Typ</InputLabel>
              <Select labelId="key-type-label" value={keyType} label="Typ" onChange={e => setKeyType(e.target.value as 'node' | 'leaf')}>
                <MenuItem value="node">Gruppe (enthält weitere Übersetzungen)</MenuItem>
                <MenuItem value="leaf">Übersetzung (enthält einen Wert)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              autoFocus
              margin="dense"
              label="Schlüsselname"
              fullWidth
              variant="outlined"
              value={newKeyName}
              onChange={e => setNewKeyName(e.target.value)}
              sx={{ mb: 2 }}
            />

            {keyType === 'leaf' && (
              <TextField
                margin="dense"
                label="Übersetzungswert"
                fullWidth
                variant="outlined"
                value={newKeyValue}
                onChange={e => setNewKeyValue(e.target.value)}
                multiline
                rows={2}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} variant="outlined" color="inherit">Abbrechen</Button>
            <Button onClick={handleAddTranslation} variant="contained" color="primary">
              Hinzufügen
            </Button>
          </DialogActions>
        </Dialog>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress />
          </Box>
        )}
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
